import React from 'react';
import { IconCalendar, IconClock, IconLayoutGridAdd, IconUsers } from '@tabler/icons-react';

import { useTranslate } from '@src/core/hooks';

import {
  SummaryItemCardHeader,
  SummaryItemCardImage,
  SummaryItemCardInfo,
  SummaryItemCardInner,
  SummaryItemCardRoot,
  SummaryItemCardTitle,
  SummaryItemFooter,
  SummaryItemMetaRow,
  SummaryItemMetaText,
  SummaryItemTotal,
  SummaryChargePrice,
  SummaryChargesList,
  SummaryChargeName,
  SummaryChargeOriginalPrice,
  SummaryChargePrices,
  SummaryChargeRow,
  SummaryChargesDetails,
  SummaryChargesSection,
  SummaryChargesTitle,
  MobileSummaryRow,
  MobileSummaryRowLabel,
  MobileSummaryRowMutedValue,
} from '../../styles';
import { SummaryItemSeller } from '../SummaryItemSeller';

import type { SummaryLineItem } from '@src/features/transactables/summary/core/summaryLineItem';
import { useSummaryItemCard } from './useSummaryItemCard';

type SummaryItemCardProps = {
  item: SummaryLineItem;
  compact?: boolean;
};

export const SummaryItemCard = (props: SummaryItemCardProps) => {
  const { item, compact = false } = props;
  const { i18n } = useTranslate();
  const { formatCents, product, tariffRows, additionalRows, schedule, discountCents, totalCents, showSeller } =
    useSummaryItemCard({ item });
  const ItemCardRoot = compact ? SummaryItemCardInner : SummaryItemCardRoot;

  return (
    <ItemCardRoot data-testid='summary-item-card'>
      <SummaryItemCardHeader>
        <SummaryItemCardInfo>
          <SummaryItemCardTitle>{product.name}</SummaryItemCardTitle>

          <SummaryItemMetaRow>
            {schedule.date ? (
              <SummaryItemMetaText>
                <IconCalendar size={20} />
                {schedule.date}
              </SummaryItemMetaText>
            ) : null}
            {schedule.time ? (
              <SummaryItemMetaText>
                <IconClock size={20} />
                {schedule.time}
              </SummaryItemMetaText>
            ) : null}
          </SummaryItemMetaRow>

          {showSeller && product?.app ? (
            <SummaryItemSeller name={product.app.name} logoUrl={product.app.logoUrl} />
          ) : null}
        </SummaryItemCardInfo>

        {!compact && product.thumbnailUrl ? (
          <SummaryItemCardImage src={product.thumbnailUrl} alt={product.name} />
        ) : null}
      </SummaryItemCardHeader>

      {tariffRows.length > 0 ? (
        <SummaryChargesSection>
          <SummaryChargesTitle>
            <IconUsers size={24} />
            {i18n('checkout_tariffs_section')}
          </SummaryChargesTitle>

          <SummaryChargesDetails>
            <SummaryChargesList>
              {tariffRows.map((tariff) => (
                <SummaryChargeRow key={tariff.key}>
                  <SummaryChargeName>{`${tariff.quantity}x ${tariff.name}`}</SummaryChargeName>
                  <SummaryChargePrices>
                    {tariff.originalCents ? (
                      <SummaryChargeOriginalPrice>
                        {`${i18n('checkout_fromValue')}${formatCents(tariff.originalCents)}`}
                      </SummaryChargeOriginalPrice>
                    ) : null}
                    <SummaryChargePrice>
                      {tariff.originalCents
                        ? `${i18n('checkout_toValue')}${formatCents(tariff.currentCents)}`
                        : formatCents(tariff.currentCents)}
                    </SummaryChargePrice>
                  </SummaryChargePrices>
                </SummaryChargeRow>
              ))}
            </SummaryChargesList>
          </SummaryChargesDetails>
        </SummaryChargesSection>
      ) : null}

      {additionalRows.length > 0 ? (
        <SummaryChargesSection data-testid='summary-item-additionals'>
          <SummaryChargesTitle>
            <IconLayoutGridAdd size={24} />
            {i18n('checkout_additionals')}
          </SummaryChargesTitle>

          <SummaryChargesDetails>
            <SummaryChargesList>
              {additionalRows.map((additional) => (
                <SummaryChargeRow key={additional.key}>
                  <SummaryChargeName>{`${additional.quantity}x ${additional.name}`}</SummaryChargeName>
                  <SummaryChargePrice>{formatCents(additional.totalCents)}</SummaryChargePrice>
                </SummaryChargeRow>
              ))}
            </SummaryChargesList>
          </SummaryChargesDetails>
        </SummaryChargesSection>
      ) : null}

      <SummaryItemFooter>
        {discountCents > 0 ? (
          <MobileSummaryRow>
            <MobileSummaryRowLabel>{i18n('checkout_discounts')}</MobileSummaryRowLabel>
            <MobileSummaryRowMutedValue>{`-${formatCents(discountCents)}`}</MobileSummaryRowMutedValue>
          </MobileSummaryRow>
        ) : null}
        <SummaryItemTotal>{formatCents(totalCents)}</SummaryItemTotal>
      </SummaryItemFooter>
    </ItemCardRoot>
  );
};

export default SummaryItemCard;
